import { put, call, takeLatest, select } from 'typed-redux-saga'
import {
	errorHandler,
	loadersSlice,
	LocalStorage,
	modalsSlice,
	notifyMessageCreator,
} from 'Modules'
import { FormNames } from 'Constants/FormNames'
import { authSelector, authSlice } from 'Pages/auth'
import { MODAL_NAME } from 'Constants/ModalName'
import { ProfileNS, profileSlice, profileSelectors, profileApi } from 'Pages/profile'
import { UPDATE_PASSWORD_ERROR } from 'Pages/profile/constants'
import { isErrorBizon } from 'Helpers'

export function* updateProfileSaga(action: ProfileNS.UpdateProfile) {
	try {
		yield* put(loadersSlice.actions.showGlobalLoader())
		const { id } = yield* select(authSelector.getAuthDataSelector)
		const avatarEditorFile = yield* select(profileSelectors.selectAvatarEditorFile)

		const user = yield* call(profileApi.updateProfile, id, {
			...(action.payload as typeof action.payload & { avatar: never }),
			...(avatarEditorFile
				? {
						avatar: avatarEditorFile.dataUrl,
						avatar_file_name: avatarEditorFile.name,
				  }
				: {}),
		})
		yield* put(authSlice.actions.setUser(user))
		yield* put(loadersSlice.actions.hideGlobalLoader())
		yield* put(profileSlice.actions.setIsEditing(false))
		yield* put(profileSlice.actions.clearAvatarEditorFile())
		yield* call(notifyMessageCreator, 'common.profile_update.success')
	} catch (error) {
		yield* put(loadersSlice.actions.hideGlobalLoader())
		yield* call(errorHandler, error, FormNames.PROFILE.MAIN)
	}
}

export function* updatePasswordSaga(action: ProfileNS.UpdatePassword) {
	try {
		yield* put(loadersSlice.actions.showGlobalLoader())
		const { data } = action.payload
		const { id } = yield* select(authSelector.getAuthDataSelector)
		const user = yield* call(profileApi.updatePassword, id, data)
		yield* call(LocalStorage.setAuth, { eo_token: user.eo_token })
		yield* put(authSlice.actions.setUser(user))
		yield* put(loadersSlice.actions.hideGlobalLoader())
		yield* put(modalsSlice.actions.hideModal(MODAL_NAME.CHANGE_PASSWORD_PROFILE))
		yield* call(notifyMessageCreator, 'common.password_update.success')
	} catch (error) {
		yield* put(loadersSlice.actions.hideGlobalLoader())
		const changeError = error.data?.errors.find((item) => item.password === UPDATE_PASSWORD_ERROR)
		if (changeError) {
			action?.payload?.errorCallback?.(error.data?.errors)
		} else {
			yield* call(errorHandler, error, FormNames.PROFILE.CHANGE_PASSWORD)
		}
	}
}

export function* updateUsernameSaga(action: ProfileNS.UpdateUsername) {
	try {
		yield* put(loadersSlice.actions.showGlobalLoader())
		const { id } = yield* select(authSelector.getAuthDataSelector)
		const user = yield* call(profileApi.updateLogin, id, action.payload)
		yield* put(authSlice.actions.setUser(user))
		yield* put(loadersSlice.actions.hideGlobalLoader())
		yield* call(notifyMessageCreator, 'common.profile_update.success')
	} catch (error) {
		yield* put(loadersSlice.actions.hideGlobalLoader())
		yield* call(errorHandler, error, FormNames.PROFILE.CHANGE_USERNAME)
	}
}

function* sendEmail(scope: string) {
	try {
		yield* put(loadersSlice.actions.showGlobalLoader())
		yield* call(profileApi.postEmailConfirmation, scope)
	} catch (error) {
		yield* call(errorHandler, error)
	} finally {
		yield* put(loadersSlice.actions.hideGlobalLoader())
	}
}
export function* initEnableTwoFactorAuth(action: ProfileNS.InitEnableTwoFactorAuth) {
	try {
		yield* put(loadersSlice.actions.showGlobalLoader())
		const { userId } = action.payload
		yield* call(profileApi.enableTwoFactorAuth, userId, null)
	} catch (error) {
		const scope = error.data?.errors[0]?.email_confirmation_code
		if (isErrorBizon(error)) {
			action.payload.errorCallback?.(error)
		} else if (scope) {
			yield* call(sendEmail, scope)
		} else {
			yield* call(errorHandler, error)
		}
	} finally {
		yield* put(loadersSlice.actions.hideGlobalLoader())
	}
}
export function* confirmEnableTwoFactorAuth(action: ProfileNS.ConfirmEnableTwoFactorAuth) {
	try {
		yield* put(loadersSlice.actions.showGlobalLoader())
		const { userId, code } = action.payload
		yield* call(profileApi.enableTwoFactorAuth, userId, code)
		const user = yield* select(authSelector.getAuthDataSelector)
		yield* put(authSlice.actions.setUser({ ...user, two_factor_auth: true }))
		yield* put(modalsSlice.actions.hideModal(MODAL_NAME.TWO_FACTOR_AUTH_ENABLE))
	} catch (error) {
		action.payload.errorCallback?.(error)
	} finally {
		yield* put(loadersSlice.actions.hideGlobalLoader())
	}
}
export function* disableTwoFactorAuth(action: ProfileNS.DisableTwoFactorAuth) {
	try {
		yield* put(loadersSlice.actions.showGlobalLoader())
		const { userId } = action.payload
		yield* call(profileApi.disableTwoFactorAuth, userId)
		const user = yield* select(authSelector.getAuthDataSelector)
		yield* put(authSlice.actions.setUser({ ...user, two_factor_auth: false }))
		yield* put(modalsSlice.actions.hideModal(MODAL_NAME.TWO_FACTOR_AUTH_DISABLE))
	} catch (error) {
		yield* call(errorHandler, error)
	} finally {
		yield* put(loadersSlice.actions.hideGlobalLoader())
	}
}

export function* profileSaga() {
	yield* takeLatest(profileSlice.actions.updateProfile, updateProfileSaga)
	yield* takeLatest(profileSlice.actions.updatePassword, updatePasswordSaga)
	yield* takeLatest(profileSlice.actions.updateUsername, updateUsernameSaga)
	yield* takeLatest(profileSlice.actions.initEnableTwoFactorAuth, initEnableTwoFactorAuth)
	yield* takeLatest(profileSlice.actions.confirmEnableTwoFactorAuth, confirmEnableTwoFactorAuth)
	yield* takeLatest(profileSlice.actions.disableTwoFactorAuth, disableTwoFactorAuth)
}
